import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  SortDirection,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown, ChevronUp, Scan } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Props<T> {
  data: T[]
  columns: ColumnDef<T>[]
  initialSorting?: SortingState
  initialState?: InitialTableState
  showFooter?: boolean
}

export function Table<T>({
  data,
  columns,
  initialSorting,
  initialState,
  showFooter,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? [])
  const [filtering, setFiltering] = useState('')

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
    state: {
      sorting,
      globalFilter: filtering,
    },
    initialState: initialState ?? {
      pagination: {
        pageSize: 10,
      },
    },
  })

  const needPagination = table.getPageCount() > 1
  const currentPage = table.getState().pagination.pageIndex + 1
  const pageCount = table.getPageCount()

  const headerGroups = table.getHeaderGroups()
  const { rows } = table.getRowModel()

  function scrollTop() {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' })
  }

  const pageHandler = {
    first: () => {
      table.setPageIndex(0)
      scrollTop()
    },
    previos: () => {
      table.previousPage()
      scrollTop()
    },
    next: () => {
      table.nextPage()
      scrollTop()
    },
    last: () => {
      table.setPageIndex(table.getPageCount() - 1)
      scrollTop()
    },
  }

  return (
    <div>
      <UITable>
        <TableHeader>
          {headerGroups.map((header) => {
            return (
              <TableRow key={header.id}>
                {header.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer bg-muted font-normal"
                    >
                      <div className="flex items-center gap-2">
                        <span>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </span>
                        {
                          {
                            asc: <ChevronUp className="h-4 w-4" />,
                            desc: <ChevronDown className="h-4 w-4" />,
                          }[header.column.getIsSorted() as SortDirection]
                        }
                      </div>
                    </TableHead>
                  )
                })}
              </TableRow>
            )
          })}
        </TableHeader>

        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>

        {showFooter && (
          <TableFooter>
            {headerGroups.map((header) => {
              return (
                <TableRow key={header.id}>
                  {header.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className="bg-muted/50 text-sm"
                      >
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableFooter>
        )}
      </UITable>

      {needPagination && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" onClick={pageHandler.first}>
              Primeira
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanPreviousPage()}
              onClick={pageHandler.previos}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanNextPage()}
              onClick={pageHandler.next}
            >
              Próxima
            </Button>
            <Button variant="outline" size="sm" onClick={pageHandler.last}>
              Última
            </Button>
          </div>

          <span className="text-sm text-muted-foreground">
            Página {currentPage} de {pageCount}
          </span>
        </div>
      )}

      {data.length === 0 ? (
        <div className="flex items-center justify-center gap-2 bg-muted p-4">
          <Scan className="h-4 w-4" strokeWidth={1} />
          <span className="text-sm">Nenhum dado encontrado</span>
        </div>
      ) : (
        <div className="p-2 text-center text-xs text-muted-foreground">
          <span>
            Total de {data.length} {data.length === 1 ? 'item' : 'itens'}
          </span>
        </div>
      )}
    </div>
  )
}
